/**
 * Help Center — category views, predictive search, and results.
 * Content is pre-rendered; JS only toggles visibility and filters.
 */
(function () {
  const ROOT_SELECTOR = '[data-help-center]';
  const INIT_ATTR = 'data-help-center-init';
  const CATEGORY_PARAM = 'category';
  const QUERY_PARAM = 'q';
  const DEBOUNCE_MS = 200;
  const PREDICTIVE_LIMIT = 8;
  const abortControllers = new WeakMap();

  const debounce = (fn, wait) => {
    let timer = null;

    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        fn(...args);
      }, wait);
    };
  };

  const getStrings = (root) => {
    const strings = {};
    const template = root.querySelector('[data-help-strings]');

    if (!template) return strings;

    const nodes = template.content
      ? template.content.querySelectorAll('[data-key]')
      : template.querySelectorAll('[data-key]');

    for (const node of nodes) {
      const key = node.getAttribute('data-key');
      if (key) strings[key] = node.textContent || '';
    }

    return strings;
  };

  const formatString = (template, replacements) => {
    let output = template || '';

    for (const [key, value] of Object.entries(replacements)) {
      output = output.split(`[[${key}]]`).join(String(value));
    }

    return output;
  };

  const buildIndex = (root) => {
    const items = [];
    const seen = new Set();
    const nodes = root.querySelectorAll('.help-center__item[data-qa-id]');

    for (const node of nodes) {
      const id = node.dataset.qaId;
      if (!id || seen.has(id)) continue;

      seen.add(id);
      items.push({
        id,
        question: node.dataset.question || '',
        search: (node.dataset.search || '').toLowerCase(),
        categoryHandle: node.dataset.categoryHandle || '',
        categoryLabel: node.dataset.categoryLabel || '',
        snippet: node.dataset.snippet || '',
      });
    }

    return items;
  };

  const teardown = (root) => {
    const controller = abortControllers.get(root);

    if (controller) {
      controller.abort();
      abortControllers.delete(root);
    }

    root.removeAttribute(INIT_ATTR);
  };

  const init = (root) => {
    teardown(root);
    root.setAttribute(INIT_ATTR, 'true');

    const controller = new AbortController();
    const { signal } = controller;
    abortControllers.set(root, controller);

    const searchInput = root.querySelector('[data-help-search]');
    const clearButton = root.querySelector('[data-help-clear]');
    const predictive = root.querySelector('[data-help-predictive]');
    const liveRegion = root.querySelector('[data-help-live]');
    const hub = root.querySelector('[data-help-view="hub"]');
    const searchView = root.querySelector('[data-help-view="search"]');
    const resultsHeading = root.querySelector('[data-help-results-heading]');
    const resultsList = root.querySelector('[data-help-results-list]');
    const noResults = root.querySelector('[data-help-no-results]');
    const categoryButtons = root.querySelectorAll('[data-help-category]');
    const panels = root.querySelectorAll('[data-help-view="category"]');
    const backButtons = root.querySelectorAll('[data-help-back]');
    const strings = getStrings(root);
    const index = buildIndex(root);

    if (!searchInput || !hub) {
      teardown(root);
      return;
    }

    let activeView = 'hub';
    let activeCategory = '';
    let activePredictiveIndex = -1;

    const panelsByHandle = new Map();

    for (const panel of panels) {
      const handle = panel.dataset.categoryHandle;
      if (handle) panelsByHandle.set(handle, panel);
    }

    const updateUrl = ({ category = '', query = '' } = {}) => {
      const url = new URL(window.location.href);

      if (category) {
        url.searchParams.set(CATEGORY_PARAM, category);
      } else {
        url.searchParams.delete(CATEGORY_PARAM);
      }

      if (query) {
        url.searchParams.set(QUERY_PARAM, query);
      } else {
        url.searchParams.delete(QUERY_PARAM);
      }

      window.history.replaceState({}, '', url.toString());
    };

    const hideAllViews = () => {
      hub.hidden = true;

      for (const panel of panels) {
        panel.hidden = true;
      }

      if (searchView) searchView.hidden = true;
    };

    const closeOpenItems = (scope = root) => {
      const openItems = scope.querySelectorAll('.help-center__item[open]');

      for (const item of openItems) {
        item.open = false;
      }
    };

    const showHub = () => {
      hideAllViews();
      closeOpenItems();
      hub.hidden = false;
      activeView = 'hub';
      activeCategory = '';
      updateUrl({ query: searchInput.value.trim() });
    };

    const showCategory = (handle, { openId = '', updateHistory = true } = {}) => {
      const panel = panelsByHandle.get(handle);
      if (!panel) return;

      hideAllViews();
      closeOpenItems();
      panel.hidden = false;
      activeView = 'category';
      activeCategory = handle;

      if (openId) {
        const item = panel.querySelector(`.help-center__item[data-qa-id="${CSS.escape(openId)}"]`);

        if (item) {
          item.open = true;
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          const summary = item.querySelector('summary');
          if (summary) summary.focus({ preventScroll: true });
        }
      }

      if (updateHistory) {
        updateUrl({ category: handle, query: searchInput.value.trim() });
      }
    };

    const filterIndex = (query) => {
      const normalized = query.trim().toLowerCase();
      if (!normalized) return [];

      const matches = [];

      for (const item of index) {
        if (item.search.includes(normalized)) {
          matches.push(item);
        }
      }

      return matches;
    };

    const closePredictive = () => {
      if (!predictive) return;

      predictive.hidden = true;
      predictive.innerHTML = '';
      searchInput.setAttribute('aria-expanded', 'false');
      activePredictiveIndex = -1;
    };

    const announce = (message) => {
      if (!liveRegion) return;
      liveRegion.textContent = message;
    };

    const renderPredictive = (matches, query) => {
      if (!predictive) return;

      if (!query.trim() || matches.length === 0) {
        closePredictive();
        if (query.trim()) {
          announce(formatString(strings.no_results, { terms: query.trim() }));
        }
        return;
      }

      const limited = matches.slice(0, PREDICTIVE_LIMIT);
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < limited.length; i += 1) {
        const item = limited[i];
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'help-center__predictive-item';
        button.setAttribute('role', 'option');
        button.setAttribute('id', `HelpPredictiveOption-${i}`);
        button.dataset.qaId = item.id;
        button.dataset.categoryHandle = item.categoryHandle;

        const question = document.createElement('span');
        question.className = 'help-center__predictive-question';
        question.textContent = item.question;

        const meta = document.createElement('span');
        meta.className = 'help-center__predictive-meta';
        meta.textContent = item.categoryLabel;

        button.append(question, meta);
        fragment.append(button);
      }

      if (matches.length > PREDICTIVE_LIMIT) {
        const footer = document.createElement('button');
        footer.type = 'button';
        footer.className = 'help-center__predictive-footer';
        footer.dataset.seeAll = 'true';
        footer.textContent = strings.see_all || 'See all results';
        fragment.append(footer);
      }

      predictive.innerHTML = '';
      predictive.append(fragment);
      predictive.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
      activePredictiveIndex = -1;
      announce(formatString(strings.suggestions_count, { count: matches.length }));
    };

    const showSearchResults = (query) => {
      if (!searchView || !resultsList || !resultsHeading) return;

      const trimmed = query.trim();
      const matches = filterIndex(trimmed);

      hideAllViews();
      closeOpenItems();
      closePredictive();
      searchView.hidden = false;
      activeView = 'search';
      activeCategory = '';

      resultsHeading.textContent = formatString(strings.results_heading, { terms: trimmed });
      resultsList.innerHTML = '';

      if (matches.length === 0) {
        if (noResults) {
          noResults.hidden = false;
          noResults.textContent = formatString(strings.no_results, { terms: trimmed });
        }
      } else {
        if (noResults) noResults.hidden = true;

        const fragment = document.createDocumentFragment();

        for (const item of matches) {
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'help-center__result-item';
          button.dataset.qaId = item.id;
          button.dataset.categoryHandle = item.categoryHandle;

          const question = document.createElement('span');
          question.className = 'help-center__result-question';
          question.textContent = item.question;

          const meta = document.createElement('span');
          meta.className = 'help-center__result-meta';
          meta.textContent = item.categoryLabel;

          button.append(question, meta);

          if (item.snippet) {
            const snippet = document.createElement('span');
            snippet.className = 'help-center__result-snippet';
            snippet.textContent = item.snippet;
            button.append(snippet);
          }

          li.append(button);
          fragment.append(li);
        }

        resultsList.append(fragment);
      }

      updateUrl({ query: trimmed });
    };

    const openArticle = (categoryHandle, qaId) => {
      closePredictive();
      showCategory(categoryHandle, { openId: qaId });
    };

    const setClearVisibility = () => {
      if (!clearButton) return;
      clearButton.hidden = searchInput.value.trim() === '';
    };

    const onSearchInput = () => {
      const query = searchInput.value;
      setClearVisibility();

      if (!query.trim()) {
        closePredictive();
        if (activeView === 'search') showHub();
        updateUrl({ category: activeCategory });
        return;
      }

      const matches = filterIndex(query);
      renderPredictive(matches, query);
    };

    const debouncedSearch = debounce(onSearchInput, DEBOUNCE_MS);

    const highlightPredictive = (nextIndex) => {
      if (!predictive || predictive.hidden) return;

      const options = predictive.querySelectorAll('.help-center__predictive-item');
      if (options.length === 0) return;

      if (activePredictiveIndex >= 0 && options[activePredictiveIndex]) {
        options[activePredictiveIndex].classList.remove('is-active');
      }

      activePredictiveIndex = nextIndex;

      if (activePredictiveIndex < 0) {
        searchInput.removeAttribute('aria-activedescendant');
        return;
      }

      if (activePredictiveIndex >= options.length) {
        activePredictiveIndex = options.length - 1;
      }

      const active = options[activePredictiveIndex];
      active.classList.add('is-active');
      searchInput.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    };

    for (const button of categoryButtons) {
      button.addEventListener(
        'click',
        () => {
          const handle = button.dataset.helpCategory;
          if (handle) showCategory(handle);
        },
        { signal }
      );
    }

    for (const button of backButtons) {
      button.addEventListener(
        'click',
        () => {
          closePredictive();
          showHub();
        },
        { signal }
      );
    }

    searchInput.addEventListener('input', debouncedSearch, { signal });

    searchInput.addEventListener(
      'keydown',
      (event) => {
        const options = predictive
          ? predictive.querySelectorAll('.help-center__predictive-item')
          : [];

        if (event.key === 'ArrowDown' && options.length > 0) {
          event.preventDefault();
          if (predictive.hidden) {
            renderPredictive(filterIndex(searchInput.value), searchInput.value);
          }
          highlightPredictive(activePredictiveIndex + 1);
          return;
        }

        if (event.key === 'ArrowUp' && options.length > 0) {
          event.preventDefault();
          highlightPredictive(activePredictiveIndex <= 0 ? -1 : activePredictiveIndex - 1);
          return;
        }

        if (event.key === 'Escape') {
          closePredictive();
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();

          if (activePredictiveIndex >= 0 && options[activePredictiveIndex]) {
            const option = options[activePredictiveIndex];
            openArticle(option.dataset.categoryHandle, option.dataset.qaId);
            return;
          }

          if (searchInput.value.trim()) {
            showSearchResults(searchInput.value);
          }
        }
      },
      { signal }
    );

    if (predictive) {
      predictive.addEventListener(
        'click',
        (event) => {
          const seeAll = event.target.closest('[data-see-all]');
          if (seeAll) {
            showSearchResults(searchInput.value);
            return;
          }

          const option = event.target.closest('.help-center__predictive-item');
          if (!option) return;

          openArticle(option.dataset.categoryHandle, option.dataset.qaId);
        },
        { signal }
      );
    }

    if (resultsList) {
      resultsList.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('.help-center__result-item');
          if (!button) return;

          openArticle(button.dataset.categoryHandle, button.dataset.qaId);
        },
        { signal }
      );
    }

    if (clearButton) {
      clearButton.addEventListener(
        'click',
        () => {
          searchInput.value = '';
          setClearVisibility();
          closePredictive();
          showHub();
          searchInput.focus();
        },
        { signal }
      );
    }

    document.addEventListener(
      'click',
      (event) => {
        const searchField = root.querySelector('.help-center__search');
        if (searchField && searchField.contains(event.target)) return;
        closePredictive();
      },
      { signal }
    );

    const params = new URLSearchParams(window.location.search);
    const initialCategory = params.get(CATEGORY_PARAM) || '';
    const initialQuery = params.get(QUERY_PARAM) || '';

    if (initialQuery) {
      searchInput.value = initialQuery;
      setClearVisibility();
      showSearchResults(initialQuery);
    } else if (initialCategory && panelsByHandle.has(initialCategory)) {
      showCategory(initialCategory, { updateHistory: false });
    } else {
      showHub();
    }
  };

  const initAll = () => {
    const roots = document.querySelectorAll(ROOT_SELECTOR);

    for (const root of roots) {
      init(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR) || event.target.closest?.(ROOT_SELECTOR);
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR) || event.target.closest?.(ROOT_SELECTOR);
    if (root) teardown(root);
  });
})();
